import {isKeyOf} from '@raison/toolbox';
import {RsnButton, RsnDialog, RsnInput, RsnRadioGroup, RsnSelect} from '@raison/ui-kit';
import BigNumber from 'bignumber.js';
import {produce} from 'immer';
import {useSetAtom} from 'jotai';
import React, {useEffect, useState} from 'react';
import {CountryPicker} from '~/entities/countries';
import {Currency, useCurrencyFormat} from '~/entities/currencies';
import {
    connectFieldWith,
    connectSubmitToForm,
    fieldBlurRecipe,
    fieldsCompleteRecipe,
    FormState,
    userInputRecipe,
    writeUserInput,
} from '~/library/form-models.v1';
import {useTrTxReceiverDataSubmit} from '../api/submitTrTxReceiverData';
import {useSearchModel} from '../lib/useSearchModel';
import {OrderId} from '../model/TrTx';
import {submittedReceiveTrTxIds$} from '../model/TrTxIdsRegistry';
import {
    getParticipantFieldsFromUser,
    OriginatorValues,
    participantIsCompany,
    TxParticipantType,
    validateTrTxOriginator,
} from '../model/TrTxParticipant';
import {useErrorHandler} from '../model/useErrorHandler';
import {UserInfoResource} from '../model/useUserInfoResource';
import {VaspPicker} from '../model/VaspPicker';
import {AcknowledgmentDialog} from './views/AcknowledgmentDialog';
import {CompanyIdInfoDialog, Disclosure, WalletTypeInfoDialog, WalletVASPInfoDialog} from './views/SendTrTx.views';

type State = ReturnType<typeof State>;
const State = () => FormState(OriginatorValues());

type Values = State['values'];

export interface ReceivingTrTxResources {
    user: UserInfoResource;
    trtx: {
        id: OrderId;
        amount: BigNumber;
        currency: Currency;
    };
}

export function ReceivingTrTxPage({resources}: {resources: ReceivingTrTxResources}) {
    const {user, trtx} = resources;
    const [formState, setFormState] = useState(State);

    const [params, searchCtl] = useSearchModel({
        dialog: DialogName,
    });
    useEffect(...searchCtl.sync);

    const setSubmittedId = useSetAtom(submittedReceiveTrTxIds$);

    const {format: formatAsSubject} = useCurrencyFormat(trtx.currency);

    const handleError = useErrorHandler();

    const {
        submitOriginator,
        data: {issues: serverIssues = {}} = {},
        isPending: isSubmitting,
    } = useTrTxReceiverDataSubmit();

    const computedForm = {...formState, serverIssues};
    const {values, hasIssues, hasServerIssues, ...form} = validateTrTxOriginator(computedForm);

    const cantSubmit = hasIssues || hasServerIssues || isSubmitting;

    const connectSubmit = connectSubmitToForm(setFormState);

    async function sendForm() {
        const res = await submitOriginator({
            values,
            orderId: trtx.id,
            user,
        }).catch(crash => {
            return handleError(crash);
        });

        if (res.ok) {
            setSubmittedId(trtx.id);
        }
    }

    const act = {
        originatorEntityChanged(value: TxParticipantType) {
            setFormState(produce(updateOriginatorEntityRecipe(value)));
        },
        userInput(update: Partial<Values>) {
            setFormState(produce(userInputRecipe(update)));
        },
        fieldBlur(field: keyof Values) {
            setFormState(produce(fieldBlurRecipe(field)));
        },
        openDialog(dialogName: DialogName) {
            searchCtl.goTo.dialog[dialogName]();
        },
        closeDialog() {
            searchCtl.undo.dialog();
            setFormState(produce(completePickerRecipe));
        },
        submittedForm: connectSubmit(async () => {
            if (cantSubmit) {
                setFormState(produce(fieldsCompleteRecipe(...(Object.keys(form.flags) as (keyof Values)[]))));
                return;
            }

            if (values.walletType === 'vasp') {
                act.openDialog(DialogName.ack);
            } else {
                await sendForm();
            }
        }),
        clickedContinueOnAck: connectSubmit(async () => {
            act.closeDialog();
            await sendForm();
        }),
    };

    function updateOriginatorEntityRecipe(participantType: TxParticipantType) {
        return (formState: State) => {
            if (participantType === TxParticipantType.self) {
                const userFields = getParticipantFieldsFromUser(user);
                writeUserInput(formState, {participantType, ...userFields});
            } else {
                writeUserInput(formState, {participantType});
            }
        };
    }

    function completePickerRecipe(formState: State) {
        const {flags} = formState;
        if (isKeyOf(PickerName, params.dialog) && !flags[params.dialog]?.completed) {
            flags[params.dialog] = {...flags[params.dialog], completed: true};
        }
    }

    const connectField = connectFieldWith(form, act.fieldBlur);

    const connectPicker = (itsName: DialogName) => ({
        opened: itsName === params.dialog,
        onDropdownToggle: (opened: boolean) => {
            if (opened) {
                act.openDialog(itsName);
            } else {
                act.closeDialog();
            }
        },
    });
    const originatorIsCompany = participantIsCompany(values, user);

    const t = {
        Heading: `Receiving ${formatAsSubject(trtx.amount)}`,
        Description: 'We were unable to obtain information about the sender. Please specify it manually.',
        ThirdPartyIndividual: 'Third-party Individual',
        ThirdPartyCompany: 'Third-party Company',
        MyOwnWallet: 'Me',
        ImSendingTo: 'Who is sending',
        OriginatorEntity: 'Originator entity',
        OriginatorFullName: originatorIsCompany ? 'Company name' : 'Full name',
        OriginatorFullNameExample: originatorIsCompany ? 'General Electric' : 'John Doe',
        DateOfBirth: 'Date of birth',
        OriginatorCity: 'City',
        OriginatorCityExample: 'Tallinn',
        OriginatorAddress: 'Address',
        OriginatorAddressExample: 'Roosikrantsi 11',
        OriginatorCountry: 'Country',
        WalletVASP: 'Wallet VASP',
        VASP: 'VASP',
        Unhosted: 'Unhosted',
        WalletType: 'Wallet type',
        Submit: 'Submit',
        AckTitle: `Receiving ${formatAsSubject(trtx.amount)}`,
        AckConsent: `By submitting this form, you acknowledge that your personal data (including name, address, and identification information) will be shared with the other provider involved in this transfer, as required under the Proceeds of Crime (Money Laundering) and Terrorist Financing Act (PCMLTFA) and associated Regulations for electronic funds transfers (EFT) and virtual currency (VC) transfers.`,
    };

    const isSelf = values.participantType === TxParticipantType.self;

    return (
        <form className='contents' onSubmit={ev => (ev.preventDefault(), act.submittedForm())}>
            <hgroup className='grid gap-2'>
                <h2 className='fv-heading-l text-neutral-100'>{t.Heading}</h2>
                <p className='fv-text-l text-neutral-90'>{t.Description}</p>
            </hgroup>
            <RsnRadioGroup
                layout='column'
                label={t.ImSendingTo}
                value={values.participantType}
                options={[
                    {label: t.ThirdPartyIndividual, value: TxParticipantType.individual},
                    {label: t.ThirdPartyCompany, value: TxParticipantType.company},
                    {label: t.MyOwnWallet, value: TxParticipantType.self},
                ]}
                onChange={value => act.originatorEntityChanged(value)}
            />

            <RsnInput
                label={
                    <RsnInput.Label subtext={`Example: ${t.OriginatorFullNameExample}`}>
                        {t.OriginatorFullName}
                    </RsnInput.Label>
                }
                value={values.participantName}
                onChange={ev => act.userInput({participantName: ev.target.value})}
                disabled={isSelf}
                {...connectField('participantName')}
            />

            <CountryPicker
                format={'alpha3'}
                label={t.OriginatorCountry}
                placeholder='Select country'
                value={values.participantCountry}
                onChange={value => act.userInput({participantCountry: value})}
                disabled={isSelf}
                {...connectPicker(DialogName.participantCountry)}
                {...connectField('participantCountry')}
            />

            <RsnInput
                label={
                    <RsnInput.Label subtext={`Example: ${t.OriginatorCityExample}`}>{t.OriginatorCity}</RsnInput.Label>
                }
                value={values.participantTown}
                onChange={ev => act.userInput({participantTown: ev.target.value})}
                disabled={isSelf}
                {...connectField('participantTown')}
            />

            <RsnInput
                label={
                    <RsnInput.Label subtext={`Example: ${t.OriginatorAddressExample}`}>
                        {t.OriginatorAddress}
                    </RsnInput.Label>
                }
                value={values.participantAddress}
                onChange={ev => act.userInput({participantAddress: ev.target.value})}
                disabled={isSelf}
                {...connectField('participantAddress')}
            />

            <RsnRadioGroup
                label={
                    <RsnInput.LabelWithInfo onInfoClick={() => act.openDialog(DialogName.walletType)}>
                        {t.WalletType}
                    </RsnInput.LabelWithInfo>
                }
                value={values.walletType}
                options={[
                    {value: 'vasp', label: t.VASP},
                    {value: 'unhosted', label: t.Unhosted},
                ]}
                onChange={value => act.userInput({walletType: value})}
            />

            <Disclosure open={values.walletType === 'vasp'}>
                <VaspPicker
                    label={
                        <RsnSelect.LabelWithInfo onInfoClick={() => act.openDialog(DialogName.vaspInfo)}>
                            {t.WalletVASP}
                        </RsnSelect.LabelWithInfo>
                    }
                    value={values.walletVasp}
                    onChange={value => act.userInput({walletVasp: value})}
                    {...connectPicker(DialogName.walletVasp)}
                    {...connectField('walletVasp')}
                />
            </Disclosure>

            <div className='mt-auto pt-4'>
                <RsnButton.Primary disabled={isSubmitting} type='submit'>
                    {t.Submit}
                </RsnButton.Primary>
            </div>

            <RsnDialog.Controller onClose={act.closeDialog}>
                {(() => {
                    switch (params.dialog) {
                        case DialogName.walletType:
                            return <WalletTypeInfoDialog />;
                        case DialogName.vaspInfo:
                            return <WalletVASPInfoDialog />;
                        case DialogName.companyID:
                            return <CompanyIdInfoDialog />;
                        case DialogName.ack:
                            return (
                                <AcknowledgmentDialog
                                    title={t.Heading}
                                    consent={t.AckConsent}
                                    disabled={isSubmitting}
                                    onContinue={act.clickedContinueOnAck}
                                />
                            );
                    }
                })()}
            </RsnDialog.Controller>
        </form>
    );
}

type PickerName = (typeof PickerName)[keyof typeof PickerName];
const PickerName = {
    participantCountry: 'participantCountry',
    walletVasp: 'walletVasp',
} satisfies {
    [K in keyof Values]?: K;
};

type DialogName = (typeof DialogName)[keyof typeof DialogName];
const DialogName = {
    ...PickerName,
    walletType: 'walletType',
    vaspInfo: 'vaspInfo',
    companyID: 'companyID',
    ack: 'ack',
} as const;
